import { getFileExtension } from '@willbooster/shared-lib';

export const APP_AUTHOR = 'WillBooster Inc.';
export const APP_DESCRIPTION = 'The next generation programming education service powered by LLMs.';
export const EMAIL_OTP_EXPIRATION_MINUTES = 10;
// Carries the sign-in page's signed OAuth query to the sign-in code mail, whose link continues the authorization.
export const OAUTH_QUERY_HEADER = 'x-exercode-oauth-query';
export const EMAIL_OTP_LENGTH = 8;
export const LOCALES = ['ja-JP', 'en-US', 'zh-CN', 'zh-TW'] as const;
export const LOCALES_INCLUDING_NON_STANDARD = ['ja-JP', 'en-US', 'zh-CN', 'zh-TW'] as const;

const isStagingOrProduction =
  process.env.NEXT_PUBLIC_WB_ENV === 'staging' || process.env.NEXT_PUBLIC_WB_ENV === 'production';
const isTest = process.env.NEXT_PUBLIC_WB_ENV === 'test';
export const DEFAULT_DEBUG_HINT_WAITING_SECONDS = isStagingOrProduction ? 180 : isTest ? 0 : 18;
export const DEFAULT_FIX_HINT_WAITING_SECONDS = isStagingOrProduction ? 300 : isTest ? 0 : 30;
export const DEFAULT_DIFF_HINT_WAITING_SECONDS = isStagingOrProduction ? 600 : isTest ? 0 : 60;
/**
 * コード編集後にクイックヒントを生成するまで待機する秒数。
 */
export const HINT_WAITING_SECONDS_AFTER_EDIT = 10;

/**
 * UI上で学習中か否かを表示する際に、学習中であると判断するための最大のアイドル時間。
 */
export const MAX_IDLE_DURATION_MS = 1000 * 60 * 5;

/**
 * The recent period in which the course monitor counts a learner's failed submissions and answer requests.
 */
export const RECENT_STRUGGLE_WINDOW_MS = 1000 * 60 * 15;

/**
 * 最後のイベントから今回のイベント発生時の期間において、アクティブと解釈される最大時間。
 */
export const MAX_ACTIVE_DURATION_MS_AFTER_LAST_EVENT = 1000 * 60 * 15;

/**
 * アクティブなイベントを検出するための最小間隔。
 */
export const MIN_INTERVAL_MS_OF_ACTIVE_EVENTS = 1000 * 60;

/**
 * タートルグラフィクス問題1種類の合格に必要な連続正解数。
 *
 * 本定数名は意味の正確さよりも短さを優先する。
 * https://github.com/WillBoosterLab/exercode/pull/570#discussion_r1601106622
 *
 * TODO: 他の定数名にも同様のコーディングスタイルを適用し次第、上のコメントを削除する。
 */
export const TURTLE_CORRECT_STREAK = 2;

/** Files under `assets/` of a course repository that are imported and served to learners (images, documents, and datasets). */
export const REPOSITORY_ASSET_FILE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'pdf',
  'csv',
  'zip',
] as const;
export const REPOSITORY_ASSET_GLOB_PATTERN = 'assets/**/*';
const repositoryAssetExtensionSet = new Set<string>(REPOSITORY_ASSET_FILE_EXTENSIONS);

/** Whether a repository-relative path (in any directory) has an importable asset extension, case-insensitively. */
export function isRepositoryAssetFile(filePath: string): boolean {
  return repositoryAssetExtensionSet.has(getFileExtension(filePath));
}

export const ALLOWED_SUBMISSION_ATTACHED_FILE_TYPES: Record<string, string[]> = {
  'image/gif': ['.gif'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

export const ALLOWED_SUBMISSION_ATTACHED_FILE_MIME_TYPES = Object.keys(ALLOWED_SUBMISSION_ATTACHED_FILE_TYPES);

export const SUBMISSION_ATTACHED_FILE_SIZE_LIMIT_KB = 10 * 1024; // 10MB

export const SCROLL_CONTAINER_ID = 'scroll-container';
export const MATERIAL_IMPROVEMENT_SECTION_ID = 'material-improvement';
// Keep the value: users' saved ruby settings are stored under it.
export const RUBY_ENABLED_STORAGE_KEY = 'rubyful-enabled';

export const REALTIME_SURVEY_EDUCATOR_POLL_INTERVAL_MS = 1000;
export const REALTIME_SURVEY_LEARNER_POLL_INTERVAL_MS = 3000;

/**
 * モニタリング画面の自動更新で、サーバーキャッシュが切れた直後に再取得するための余裕。
 */
export const COURSE_MONITOR_REFETCH_MARGIN_MS = 3000;
/**
 * モニタリング画面の自動更新の最短間隔。サーバーキャッシュが無効な環境で連続ポーリングになるのを防ぐ。
 */
export const COURSE_MONITOR_MIN_REFETCH_INTERVAL_MS = 10_000;
